import { isArrayNullOrEmpty } from "../../helper/helper";
import { Landplot } from "../organisation/Landplot";
import { Organisation, Position } from "../organisation/OrganisationModelV2";
import { Person } from "../person/Person";

export class ContractUnileverStatement {
  statement_id: number;
  farmer_transactor_id: number;
  agent_transactor_id: number | null;
  plot_id: number;
  label: string;
  UL_status: boolean;
  UL_polygon: any;
  pickup_location?: Position;
  farmer?: Person
  land_plot?: Landplot
  quantity?: number

  constructor(data: any) {
    this.statement_id = data.statement_id;
    this.farmer_transactor_id = data.farmer_transactor_id;
    this.agent_transactor_id = data.agent_transactor_id;
    this.plot_id = data.plot_id;
    this.label = data.label;
    this.UL_status = data.UL_status;
    this.UL_polygon = data.UL_polygon;
    // The API sometimes sends a pickup_location without usable coordinates
    this.pickup_location = Position.isValid(data.pickup_location) ? data.pickup_location : undefined;
    this.farmer = this.farmer_transactor_id ? Person.build({ id: this.farmer_transactor_id }) : undefined
    this.land_plot = this.plot_id ? Landplot.build({
      id: data.plot_id,
      polygon: data.UL_polygon,
      isPlotInProgram: data.UL_status
    }) : undefined
    this.quantity = data.PALM_0002_quantity
  }

  isUl(): boolean {
    return this.UL_status
  }

  isNotUl(): boolean {
    return this.UL_status !== true
  }

  static build(d: any): ContractUnileverStatement {
    return new ContractUnileverStatement(d)
  }
}

export class ContractUnileverProccessData {

  farmerIds: number[]
  ulFarmerIds: number[]
  nonUlFarmerIds: number[]

  farmersDeclaredULLandplot: Person[]
  farmersDeclaredNonULLandplot: Person[]
  farmersUndeclared: Person[]

  agents: Person[]

  harvests: ContractUnileverStatement[]
  ulHarvest: ContractUnileverStatement[]
  nonUlHarvest: ContractUnileverStatement[]

  landplots: Landplot[]
  ulLandplots: Landplot[]
  nonUlLandplots: Landplot[]

  constructor(contract: ContractUnilever) {
    this.landplots = this.getLandplot(contract)
    this.ulLandplots = this.getUlLandplot(contract)
    this.nonUlLandplots = this.getNonUlLandplot(contract)

    this.farmerIds = this.getFarmerPersonIds(contract);
    this.ulFarmerIds = this.getULFarmerPersonIds(contract);
    this.nonUlFarmerIds = this.getNonULFarmerPersonIds(contract);

    this.farmersDeclaredULLandplot = this.getFarmersDeclaredULLandplot(contract);
    this.farmersDeclaredNonULLandplot = this.getFarmersDeclaredUlLandplots(contract);
    this.farmersUndeclared = this.getFarmersUndeclared(contract);

    this.harvests = this.getHarvest(contract)
    this.ulHarvest = this.getUlHarvest(contract)
    this.nonUlHarvest = this.getNonUlHarvest(contract)
  }

  getFarmerPersonIds(contract: ContractUnilever): number[] {
    return contract.statement.filter(f => f.farmer).map(s => s.farmer_transactor_id)
  }

  getULFarmerPersonIds(contract: ContractUnilever): number[] {
    return contract.statement.filter(s => s.isUl() && s.farmer).map(s => s.farmer_transactor_id)
  }

  getNonULFarmerPersonIds(contract: ContractUnilever): number[] {
    return contract.statement.filter(s => s.isNotUl() && s.farmer).map(s => s.farmer_transactor_id)
  }

  getFarmersDeclaredULLandplot(contract: ContractUnilever): Person[] {
    return contract.statement.filter(s => s.isUl() && s.farmer && !isArrayNullOrEmpty(this.landplots)).map(s => s.farmer)
  }

  getFarmersDeclaredUlLandplots(contract: ContractUnilever): Person[] {
    return contract.statement.filter(s => s.isNotUl() && s.farmer && !isArrayNullOrEmpty(this.landplots)).map(s => s.farmer)
  }
  getFarmersUndeclared(contract: ContractUnilever): Person[] {
    return contract.statement.filter(s => s.farmer && isArrayNullOrEmpty(this.landplots)).map(s => s.farmer)
  }

  getHarvest(contract: ContractUnilever): ContractUnileverStatement[] {
    return contract.statement
  }

  getUlHarvest(contract: ContractUnilever): ContractUnileverStatement[] {
    return contract.statement.filter(s => s.isUl())
  }

  getNonUlHarvest(contract: ContractUnilever): ContractUnileverStatement[] {
    return contract.statement.filter(s => s.isNotUl())
  }

  getLandplot(contract: ContractUnilever): Landplot[] {
    return contract.statement.filter(s => s.land_plot).map(s => s.land_plot)
  }

  getUlLandplot(contract: ContractUnilever): Landplot[] {
    return contract.statement.filter(s => s.land_plot && s.isUl()).map(s => s.land_plot)
  }

  getNonUlLandplot(contract: ContractUnilever): Landplot[] {
    return contract.statement.filter(s => s.land_plot && s.isNotUl()).map(s => s.land_plot)
  }

  static build(d: ContractUnilever): ContractUnileverProccessData {
    return new ContractUnileverProccessData(d)
  }
}

export class ContractUnilever {
  bonusAsPercGmv: number;
  bonusPaid?: number;
  buyerId: number;
  contractId: number;
  contractType: string;
  createdAt: string | null;
  day: string;
  district: string;
  name: string;
  nonUlQuantity: number;
  pricePaid: number;
  province: string;
  statement: ContractUnileverStatement[];
  subdistrict: string;
  uLQuantity: number;
  updatedAt: string | null;
  org?: Organisation
  processData?: ContractUnileverProccessData

  constructor(data: any) {
    this.bonusAsPercGmv = data.bonus_as_perc_gmv;
    this.bonusPaid = data.bonus_paid;
    this.buyerId = data.buyer_id;
    this.contractId = data.contract_id;
    this.contractType = data.contract_type;
    this.createdAt = data._created_at;
    this.day = data.day;
    this.district = data.district;
    this.name = data.name;
    this.nonUlQuantity = data.non_ul_quantity;
    this.pricePaid = data.price_paid;
    this.province = data.province;
    this.statement = (data.statement || []).map((s: any) => new ContractUnileverStatement(s));
    this.subdistrict = data.subdistrict;
    this.uLQuantity = data.ul_quantity;
    this.updatedAt = data._updated_at;
    this.processData = ContractUnileverProccessData.build(this)
    if (data.buyer_id === 55073) {
      this.org = Organisation.build({
        id: data.buyer_id,
        name: data.name,
        position: {
          coordinates: [99.34399, 3.13074]
        }
      })
    } else if (data.buyer_id === 47897) {
      this.org = Organisation.build({
        id: data.buyer_id,
        name: data.name,
        position: {
          coordinates: [99.21690166, 2.999591581]
        }
      })
    }
    else if (data?.mill_gps_position?.coordinates) {
      this.org = Organisation.build({
        id: data.buyer_id,
        name: data.name,
        position: {
          coordinates: data?.mill_gps_position?.coordinates
        }
      })
    }
  }

  static build(d: any): ContractUnilever {
    return new ContractUnilever(d)
  }

  getMarketDay(): string {
    return this.day
  }

  getFruitQuantity(): number {
    return this.getUnileverFruitQuantity() + this.getNonUnileverFruitQuantity()
  }

  getUnileverFruitQuantity(): number {
    return this.uLQuantity
  }

  getNonUnileverFruitQuantity(): number {
    return this.nonUlQuantity
  }

  isUnileverContract(): boolean {
    return this.bonusPaid > 0
  }

  getFarmerPersonIds(): number[] {
    return this.processData.farmerIds ?? []
  }

  getFarmersDeclaredUlLandplot(): Person[] {
    return this.processData.farmersDeclaredULLandplot ?? []
  }

  getFarmersDeclaredNonUlLandplot(): Person[] {
    return this.processData.farmersDeclaredNonULLandplot ?? []
  }

  getFarmersUndeclared(): Person[] {
    return this.processData.farmersUndeclared ?? []
  }


  getUnileverFarmerPersonIds(): number[] {
    return this.processData.ulFarmerIds ?? []
  }

  getNonUnileverFarmerPersonIds(): number[] {
    return this.processData.nonUlFarmerIds ?? []
  }

  getLandPlotsId(): number[] {
    return this.processData.landplots?.map(l => l.id) ?? []
  }

  getUnileverLandPlotsId(): number[] {
    return this.processData.ulLandplots?.map(l => l.id) ?? []
  }

  getNonUnileverLandPlotsId(): number[] {
    return this.processData.nonUlLandplots?.map(l => l.id) ?? []
  }

  getLandPlots(): Landplot[] {
    return this.processData.landplots ?? []
  }

  getUnileverLandPlots(): Landplot[] {
    return this.processData.ulLandplots ?? []
  }

  getUnileverHarvest(): ContractUnileverStatement[] {
    return this.processData.ulHarvest ?? []
  }

  getNonUnileverHarvest(): ContractUnileverStatement[] {
    return this.processData.nonUlHarvest ?? []
  }

  getHarvests(): ContractUnileverStatement[] {
    return this.processData.harvests ?? []
  }

  getPpunch(): number {
    return this.bonusPaid
  }

  isContractPpunched(): boolean {
    return !this.bonusPaid && this.bonusPaid > 0
  }

  getTotalNetRp(): number {
    return this.pricePaid
  }

  traceabilityLabel(): string | undefined {
    return this.statement?.at(0)?.label?.toLowerCase();
  }

  agentTransactorId(): number | undefined {
    return this.statement?.at(0)?.agent_transactor_id;
  }
}
